import React, { useState, useEffect, useRef } from 'react';
import { Alert } from '@mui/material';
import { createChatSession, getMessages, sendMessage } from '../../../services/chatService';
import { Message as MessageType, Session, UIMessage } from '../../../types/chat';
import { Agent } from '../../../types/agent';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import '../styles/chat-popup.css';

interface ChatPopupProps {
  agentId: number;
  sessionId: number | null;
  agent: Agent | null;
  onClose: () => void;
  onSessionCreated: (session: Session) => void;
}

const ChatPopup: React.FC<ChatPopupProps> = ({
  agentId,
  sessionId,
  agent,
  onClose,
  onSessionCreated
}) => {
  const { currentWorkspace } = useWorkspace();
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(sessionId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasInitializedRef = useRef(false);

  // Initialize chat session or load existing one
  useEffect(() => {
    const initializeChat = async () => {
      if (hasInitializedRef.current) return;
      hasInitializedRef.current = true;

      try {
        if (sessionId) {
          // Load existing session
          const response = await getMessages(sessionId);
          if (response.success) {
            const formattedMessages = response.data.flatMap((msg: MessageType) => {
              const messages = [];
              if (msg.question) {
                messages.push({
                  id: msg.id * 2,
                  text: msg.question,
                  sender: 'user' as const,
                  timestamp: msg.created_at
                });
              }
              if (msg.answer) {
                messages.push({
                  id: msg.id * 2 + 1,
                  text: msg.answer,
                  sender: 'ai' as const,
                  timestamp: msg.created_at
                });
              }
              return messages;
            });
            setMessages(formattedMessages);
          }
        } else {
          // Create new session
          // ✅ CRITICAL: Pass workspace_id for proper token accounting
          const response = await createChatSession(
            agentId,
            null,
            currentWorkspace?.id
          );
          if (response.success) {
            setCurrentSessionId(response.data.id);
            onSessionCreated(response.data);
          }
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Falha ao inicializar chat');
      }
    };

    initializeChat();
  }, [sessionId, agentId, onSessionCreated]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle sending a message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentSessionId || isLoading) return;

    setIsLoading(true);
    const messageText = newMessage;
    setNewMessage('');

    // Add user message
    const userMessage: UIMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    
    // Add initial AI message placeholder
    const aiMessage: UIMessage = {
      id: Date.now() + 1,
      text: '',
      sender: 'ai',
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage, aiMessage]);

    try {
      // Send message using our centralized service
      const response = await sendMessage(currentSessionId, messageText);
      
      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(5));
                  if (!data.done) {
                    // Update the AI message text
                    setMessages(prevMessages => {
                      const updatedMessages = [...prevMessages];
                      const lastIndex = updatedMessages.length - 1;
                      const lastMessage = updatedMessages[lastIndex];
                      if (lastMessage && lastMessage.sender === 'ai') {
                        updatedMessages[lastIndex] = {
                          ...lastMessage,
                          text: lastMessage.text + data.content
                        };
                      }
                      return updatedMessages;
                    });
                  }
                } catch (error) {
                  console.error('Error parsing SSE data:', error);
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Falha ao enviar mensagem');
      // Remove the AI message if there was an error
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== aiMessage.id));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="tais-chat-popup__backdrop" onClick={onClose} />
      <div className="tais-chat-popup">
        <div className="tais-chat-popup__header">
          <h2>{agent?.name || 'Chat'}</h2>
          <button className="tais-chat-popup__close" onClick={onClose}>✕</button>
        </div>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              backgroundColor: 'rgba(211, 47, 47, 0.1)', 
              color: '#ff4444',
              '.MuiAlert-icon': {
                color: '#ff4444'
              }
            }}
          >
            {error}
          </Alert>
        )}

        <div className="tais-chat-popup__messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`tais-chat-popup__message ${message.sender}`}
            >
              <div className="tais-chat-popup__message-content">
                {message.text}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSendMessage} className="tais-chat-popup__input">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
            disabled={isLoading}
          />
          <button type="submit" disabled={isLoading || !newMessage.trim()}>
            {isLoading ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </>
  );
};

export default ChatPopup; 